import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Search, ExternalLink, Filter, Award, TrendingUp, Plus, Sparkles, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const LEVEL_COLORS = {
  iniciante: { bg: 'bg-green-500/10', text: 'text-green-600 dark:text-green-400', border: 'border-green-500/30' },
  intermediario: { bg: 'bg-yellow-500/10', text: 'text-yellow-600 dark:text-yellow-400', border: 'border-yellow-500/30' },
  avancado: { bg: 'bg-red-500/10', text: 'text-red-600 dark:text-red-400', border: 'border-red-500/30' }
};

const CATEGORIES = ['Todos', 'Texto', 'Imagem', 'Vídeo', 'Código', 'Áudio', 'Automação', 'Pesquisa', 'Apresentação', 'Multimodal', 'Geral'];

export default function AITools({ user }) {
  const navigate = useNavigate();
  const [tools, setTools] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [selectedTool, setSelectedTool] = useState(null);
  const [ratings, setRatings] = useState({});
  const [userLevel, setUserLevel] = useState(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitForm, setSubmitForm] = useState({ name: '', url: '', category: 'Geral' });
  const [submitting, setSubmitting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [toolsRes, levelRes] = await Promise.all([
          axios.get(`${API}/ai-tools`, { withCredentials: true }),
          axios.get(`${API}/users/level`, { withCredentials: true })
        ]);
        setTools(toolsRes.data);
        setUserLevel(levelRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedTool) {
      const fetchRating = async () => {
        try {
          const response = await axios.get(`${API}/ai-tools/${selectedTool.tool_id}/rating`, {
            withCredentials: true
          });
          setRatings({
            use_frequency: response.data.use_frequency || 0,
            liked: response.data.liked || 0,
            know_how: response.data.know_how || 0
          });
        } catch (error) {
          console.error('Error fetching rating:', error);
        }
      };
      fetchRating();
    }
  }, [selectedTool]);

  const handleRatingSubmit = async () => {
    if (!selectedTool) return;

    try {
      await axios.post(
        `${API}/ai-tools/${selectedTool.tool_id}/rate`,
        ratings,
        { withCredentials: true, params: ratings }
      );
      toast.success('Avaliação salva com sucesso!');
      setSelectedTool(null);

      const levelRes = await axios.get(`${API}/users/level`, { withCredentials: true });
      setUserLevel(levelRes.data);
    } catch (error) {
      console.error('Error saving rating:', error);
      toast.error('Erro ao salvar avaliação');
    }
  };

  const handleSubmitTool = async () => {
    if (!submitForm.name.trim()) {
      toast.error('Digite o nome da ferramenta');
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/ai-tools/submit`, submitForm, { withCredentials: true });
      const newTool = response.data;
      setTools(prev => [...prev, newTool]);
      setShowSubmitModal(false);
      setSubmitForm({ name: '', url: '', category: 'Geral' });
      toast.success('Ferramenta adicionada! A ficha foi gerada pela IA.');
      // Navigate to the new tool's detail page
      navigate(`/ai-tools/${newTool.tool_id}`);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao adicionar ferramenta';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredTools = tools
    .filter(tool => {
      const matchesSearch = tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tool.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (tool.category || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'Todos' || tool.category === selectedCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      if (a.is_featured && !b.is_featured) return -1;
      if (!a.is_featured && b.is_featured) return 1;
      return 0;
    });

  const getLevelLabel = (level) => {
    const labels = {
      iniciante: 'Iniciante',
      intermediario: 'Intermediário',
      avancado: 'Avançado'
    };
    return labels[level] || 'Iniciante';
  };

  const knowledgePercentage = userLevel ? userLevel.score : 0;
  const toolsEvaluated = userLevel ? userLevel.tools_rated : 0;

  return (
    <div className="min-h-screen">
      <Navigation user={user} />

      <div
        className="relative h-[200px] bg-cover bg-center"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1678957949479-b1e876bee3f1?crop=entropy&cs=srgb&fm=jpg&q=85')",
        }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 container mx-auto px-4 md:px-8 h-full flex items-center justify-between">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="ai-tools-title">
              Ferramentas de IA
            </h1>
            <p className="text-white/90">Conheça, avalie e adicione ferramentas do mercado</p>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <Button
              onClick={() => setShowSubmitModal(true)}
              className="rounded-none font-medium tracking-wide uppercase text-xs bg-white text-black hover:bg-white/90"
              data-testid="add-tool-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Adicionar Ferramenta
            </Button>
            <a
              href="https://nextbestai.io"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-6 py-3 bg-white/10 text-white font-medium tracking-wide uppercase text-xs hover:bg-white/20 transition-colors border border-white/20"
              data-testid="nbai-link"
            >
              NBAI <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>

      <main className="container mx-auto px-4 md:px-8 py-16">
        <div className="tech-card-shadow p-8 rounded-none border border-white/5 relative corner-accent mb-8" data-testid="knowledge-index">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex items-center gap-5">
              <div className="p-3 bg-primary/10 text-primary border border-primary/20">
                <Award className="h-6 w-6" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest mb-1">Expertise_Level</p>
                <h3 className="text-3xl font-bold font-heading uppercase text-white">{userLevel?.level || 'Iniciante'}</h3>
              </div>
            </div>

            <div className="flex items-center gap-5">
              <div className="p-3 bg-primary/10 text-primary border border-primary/20">
                <TrendingUp className="h-6 w-6" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest mb-1">Evaluated_Tools</p>
                <h3 className="text-3xl font-bold font-heading text-white">{toolsEvaluated}/{tools.length}</h3>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-end mb-3">
                <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest">IQ_Index</p>
                <span className="text-3xl font-bold font-mono text-primary leading-none">{knowledgePercentage.toFixed(0)}%</span>
              </div>
              <div className="w-full bg-white/5 h-1.5 overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-1000"
                  style={{ width: `${knowledgePercentage}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Search + Filters + Mobile Add */}
        <div className="mb-6 space-y-4">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar ferramentas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-tools-input"
                className="pl-10 rounded-none border-input"
              />
            </div>
            <Button
              variant="outline"
              className="rounded-none font-medium tracking-wide uppercase text-xs"
              data-testid="filter-button"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filtros
            </Button>
            <Button
              onClick={() => setShowSubmitModal(true)}
              className="md:hidden rounded-none font-medium tracking-wide uppercase text-xs"
            >
              <Plus className="h-4 w-4 mr-2" /> Nova
            </Button>
          </div>

          {/* Category filters */}
          {showFilters && (
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1 text-xs uppercase tracking-wide font-medium border rounded-none transition-colors ${selectedCategory === cat
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-transparent border-border hover:border-primary/50'
                    }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {filteredTools.map((tool) => {
            const levelColor = LEVEL_COLORS[tool.level] || LEVEL_COLORS.iniciante;

            return (
              <Card
                key={tool.tool_id}
                className="h-full flex flex-col justify-between p-4 border border-border hover:border-primary/50 transition-all hover:shadow-lg hover:-translate-y-0.5 rounded-none cursor-pointer group"
                onClick={() => navigate(`/ai-tools/${tool.tool_id}`)}
                data-testid={`tool-card-${tool.tool_id}`}
              >
                <div>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {tool.logo_url && (
                        <img
                          src={tool.logo_url}
                          alt={tool.name}
                          className="h-10 w-10 object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <h3 className="font-bold text-lg group-hover:text-primary transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>{tool.name}</h3>
                          {tool.is_featured && (
                            <span className="text-[10px] font-mono px-2 py-0.5 bg-primary/20 text-primary border border-primary/30 uppercase tracking-tighter shrink-0">
                              Workshop
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 mb-3 flex-wrap">
                    {tool.is_free && (
                      <span className="text-xs px-2 py-1 bg-muted rounded-none uppercase tracking-wide">
                        Free
                      </span>
                    )}
                    <span className={`text-xs px-2 py-1 rounded-none uppercase tracking-wide border ${levelColor.bg} ${levelColor.text} ${levelColor.border}`}>
                      {getLevelLabel(tool.level)}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-3">{tool.description}</p>
                  {tool.main_features && tool.main_features.length > 0 && (
                    <>
                      <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Principais Features</div>
                      <ul className="space-y-1 mb-4">
                        {tool.main_features.slice(0, 3).map((feature, index) => (
                          <li key={index} className="text-xs flex items-start gap-1">
                            <span className="text-muted-foreground">•</span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-wide px-2 py-1 bg-muted inline-block rounded-none">
                    {tool.category}
                  </div>
                  <span className="text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity uppercase tracking-wide font-medium">
                    Ver ficha →
                  </span>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Quick Rating Modal (kept for backward compatibility) */}
        {selectedTool && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedTool(null)}>
            <Card className="max-w-2xl w-full p-8 rounded-none" onClick={(e) => e.stopPropagation()} data-testid="rating-modal">
              <div className="flex items-start gap-4 mb-4">
                {selectedTool.logo_url && (
                  <img
                    src={selectedTool.logo_url}
                    alt={selectedTool.name}
                    className="h-16 w-16 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
                <div>
                  <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{selectedTool.name}</h2>
                  <div className="flex gap-2 mb-2">
                    {selectedTool.is_free && (
                      <span className="text-xs px-2 py-1 bg-muted rounded-none uppercase tracking-wide">
                        Free
                      </span>
                    )}
                    {selectedTool.level && (
                      <span className={`text-xs px-2 py-1 rounded-none uppercase tracking-wide border ${LEVEL_COLORS[selectedTool.level].bg} ${LEVEL_COLORS[selectedTool.level].text} ${LEVEL_COLORS[selectedTool.level].border}`}>
                        {getLevelLabel(selectedTool.level)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <p className="text-muted-foreground mb-6">{selectedTool.description}</p>

              {selectedTool.url && (
                <a
                  href={selectedTool.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium uppercase tracking-wide hover:underline mb-6"
                >
                  Visitar Site <ExternalLink className="h-4 w-4" />
                </a>
              )}

              <div className="space-y-6 mb-8">
                <div>
                  <label className="text-sm font-medium mb-2 block">Uso Muito (0-5)</label>
                  <Slider
                    value={[ratings.use_frequency || 0]}
                    onValueChange={([value]) => setRatings({ ...ratings, use_frequency: value })}
                    max={5}
                    step={1}
                    className="mb-2"
                    data-testid="rating-use-frequency"
                  />
                  <div className="text-right text-sm text-muted-foreground">{ratings.use_frequency || 0}</div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Gostei (0-5)</label>
                  <Slider
                    value={[ratings.liked || 0]}
                    onValueChange={([value]) => setRatings({ ...ratings, liked: value })}
                    max={5}
                    step={1}
                    className="mb-2"
                    data-testid="rating-liked"
                  />
                  <div className="text-right text-sm text-muted-foreground">{ratings.liked || 0}</div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Sei usar/Explorei (0-5)</label>
                  <Slider
                    value={[ratings.know_how || 0]}
                    onValueChange={([value]) => setRatings({ ...ratings, know_how: value })}
                    max={5}
                    step={1}
                    className="mb-2"
                    data-testid="rating-know-how"
                  />
                  <div className="text-right text-sm text-muted-foreground">{ratings.know_how || 0}</div>
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  variant="outline"
                  onClick={() => setSelectedTool(null)}
                  className="flex-1 rounded-none font-medium tracking-wide uppercase text-xs"
                  data-testid="rating-cancel"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleRatingSubmit}
                  className="flex-1 rounded-none font-medium tracking-wide uppercase text-xs"
                  data-testid="rating-submit"
                >
                  Salvar Avaliação
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Submit Tool Modal */}
        {showSubmitModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !submitting && setShowSubmitModal(false)}>
            <Card className="max-w-lg w-full p-8 rounded-none" onClick={(e) => e.stopPropagation()} data-testid="submit-tool-modal">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-primary/10 rounded-none">
                  <Sparkles className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Adicionar Ferramenta</h2>
                  <p className="text-sm text-muted-foreground">A IA vai gerar a ficha automaticamente</p>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="text-sm font-medium mb-2 block">Nome da Ferramenta *</label>
                  <Input
                    value={submitForm.name}
                    onChange={e => setSubmitForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Ex: ChatGPT, Midjourney, Claude..."
                    className="rounded-none"
                    disabled={submitting}
                    data-testid="submit-tool-name"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">URL do Site</label>
                  <Input
                    value={submitForm.url}
                    onChange={e => setSubmitForm(f => ({ ...f, url: e.target.value }))}
                    placeholder="https://..."
                    className="rounded-none"
                    disabled={submitting}
                    data-testid="submit-tool-url"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Categoria</label>
                  <select
                    value={submitForm.category}
                    onChange={e => setSubmitForm(f => ({ ...f, category: e.target.value }))}
                    className="w-full h-10 px-3 border border-input bg-background text-sm rounded-none"
                    disabled={submitting}
                    data-testid="submit-tool-category"
                  >
                    {CATEGORIES.filter(c => c !== 'Todos').map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              {submitting && (
                <div className="flex items-center gap-3 p-4 mb-4 bg-primary/5 border border-primary/20 rounded-none">
                  <Loader2 className="h-5 w-5 text-primary animate-spin" />
                  <div>
                    <p className="text-sm font-medium">Gerando ficha com IA...</p>
                    <p className="text-xs text-muted-foreground">Isso pode levar alguns segundos</p>
                  </div>
                </div>
              )}

              <div className="flex gap-4">
                <Button
                  variant="outline"
                  onClick={() => setShowSubmitModal(false)}
                  className="flex-1 rounded-none font-medium tracking-wide uppercase text-xs"
                  disabled={submitting}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSubmitTool}
                  className="flex-1 rounded-none font-medium tracking-wide uppercase text-xs"
                  disabled={submitting}
                  data-testid="submit-tool-confirm"
                >
                  {submitting ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processando...</>
                  ) : (
                    <><Sparkles className="h-4 w-4 mr-2" /> Gerar Ficha com IA</>
                  )}
                </Button>
              </div>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
